import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../../../shared/toastMessages/toastProvider';
import { Typography, Box, Stack, Paper } from '@mui/material';

import TableComponent from "./searchTable/table";
import SearchMailing from "./searchMailing/searchMailing";
import EmailList from './searchMailing/searchEmail';

import getApiData from '../../../../hooks/apiRequests/getRequest';
import ModalComponent from '../../../shared/modal/modal';

import { theme } from '../../../../configs/theme';

const PAGE_SIZE = 10;

const traderCount = (data) =>
    (data?.value || []).reduce((sum, commodity) => sum + (commodity.Exports || []).length, 0);
const SEARCH_TYPES = ['hs6Code', 'hs6Desc', 'postcode', 'company'];

const cardSx = {
    bgcolor: '#fff',
    borderRadius: '15px',
    boxShadow: '1px 5px 5px 1px rgba(0,0,0,0.12)',
    p: { xs: 2, md: 2.5 },
};

const isAbortError = (error) =>
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError' ||
    error?.message === 'The user aborted a request.';

const AddMailing = ({ headerData }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState();
    const [progress, setProgress] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedTrader, setSelectedTrader] = useState(null);
    const [refreshToken, setRefreshToken] = useState(0);
    const { showToast } = useToast();
    const showToastRef = useRef(showToast);
    const requestIdRef = useRef(0);

    useEffect(() => {
        showToastRef.current = showToast;
    }, [showToast]);

    const type = searchParams.get('type') || '';
    const query = searchParams.get('query') || '';
    const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
    const isFake = searchParams.get('fake') === '1';
    const hasActiveSearch = isFake || (SEARCH_TYPES.includes(type) && Boolean(query.trim()));
    const hasRows = Boolean(data?.value?.length);

    useEffect(() => {
        const controller = new AbortController();
        const requestId = ++requestIdRef.current;

        const runSearch = async () => {
            if (isFake) {
                setProgress(true);
                try {
                    const response = await getApiData(`/Commodity/Fake`, {
                        signal: controller.signal,
                        timeout: 0,
                    });
                    if (requestId !== requestIdRef.current) return;

                    if (response?.data) {
                        setData(response.data);
                        showToastRef.current("Sample data loaded.", "success");
                    } else {
                        showToastRef.current("Something went wrong. Please try again.", "error");
                    }
                } catch (error) {
                    if (requestId !== requestIdRef.current || isAbortError(error)) return;
                    console.error('Error fetching sample data:', error);
                    showToastRef.current("Something went wrong. Please try again later.", "error");
                } finally {
                    if (
                        requestId === requestIdRef.current &&
                        !controller.signal.aborted
                    ) {
                        setProgress(false);
                    }
                }
                return;
            }

            if (!SEARCH_TYPES.includes(type) || !query.trim()) {
                return;
            }

            const offset = (page - 1) * PAGE_SIZE;
            const filterString =
                `${type}=${encodeURIComponent(query.trim())}&limit=${PAGE_SIZE}&offset=${offset}`;

            // One request waits for a full page of 10, still under the 30s cutoff.
            // The table updates only when that page is ready.
            setProgress(true);
            setData(undefined);

            try {
                const response = await getApiData(`/Commodity?${filterString}`, {
                    signal: controller.signal,
                    timeout: 25000,
                });
                if (requestId !== requestIdRef.current) return;
                if (!response?.data) {
                    showToastRef.current("Something went wrong. Please try again later.", "error");
                    return;
                }
                setData(response.data);
                const count = traderCount(response.data);
                if (count > 0) {
                    showToastRef.current("Search results loaded.", "success");
                } else {
                    showToastRef.current("No verified companies found for that search.", "success");
                }
            } catch (error) {
                if (requestId !== requestIdRef.current || isAbortError(error)) return;
                console.error("Error fetching data:", error);
                showToastRef.current("Something went wrong. Please try again later.", "error");
            } finally {
                if (
                    requestId === requestIdRef.current &&
                    !controller.signal.aborted
                ) {
                    setProgress(false);
                }
            }
        };

        runSearch();

        return () => {
            // Abort the in-flight HTTP call so Strict Mode remounts / page
            // changes cannot apply stale results from a superseded request.
            controller.abort();
        };
    }, [type, query, page, isFake, refreshToken]);

    const handleSearch = ({ type: nextType, query: nextQuery }) => {
        if (progress) return;

        const trimmedQuery = nextQuery.trim();
        const sameSearch =
            !isFake &&
            type === nextType &&
            query.trim() === trimmedQuery &&
            page === 1;

        setSearchParams({
            type: nextType,
            query: trimmedQuery,
            page: '1',
        });

        // Same filters: force a refresh so Search can re-run the request
        if (sameSearch) {
            setRefreshToken((token) => token + 1);
        }
    };

    const handlePageChange = (nextPage) => {
        if (progress) return;
        if (!SEARCH_TYPES.includes(type) || !query.trim() || nextPage < 1) return;

        setSearchParams({
            type,
            query: query.trim(),
            page: String(nextPage),
        });
    };

    const handleFakeSearch = () => {
        if (progress) return;

        if (isFake) {
            setRefreshToken((token) => token + 1);
            return;
        }

        setSearchParams({ fake: '1' });
    };

    const handleAddTrader = (trader) => {
        setSelectedTrader({
            companyName: trader.companyName,
            postcode: trader.postcode || "N/A",
            domain: trader.domain,
            emails: Array.isArray(trader.emails) ? trader.emails : [],
            emailState: trader.emailState || "",
        });
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
        setSelectedTrader(null);
    };

    const patchTraderEmailState = useCallback((companyName, emailState) => {
        if (!companyName) return;
        const target = companyName.trim().toLowerCase();
        const nextState = emailState || '';

        setData((prev) => {
            if (!prev?.value?.length) return prev;

            let changed = false;
            const value = prev.value.map((commodity) => {
                const exports = (commodity.Exports || []).map((exp) => {
                    const name = exp?.Trader?.CompanyName?.trim()?.toLowerCase();
                    if (name !== target) return exp;

                    const current = exp.Trader.EmailState || '';
                    if (current === nextState) return exp;
                    // Don't downgrade "added" when clearing or setting a failure status.
                    if (current === 'added' && nextState !== 'added') return exp;
                    // Keep blocked domain status unless a stronger outcome is set.
                    if (
                      current === 'blocked' &&
                      (nextState === '' || nextState === 'no_emails_found')
                    ) {
                      return exp;
                    }
                    changed = true;
                    return {
                        ...exp,
                        Trader: {
                            ...exp.Trader,
                            EmailState: nextState,
                        },
                    };
                });
                return { ...commodity, Exports: exports };
            });

            return changed ? { ...prev, value } : prev;
        });
    }, []);

    return (
        <Box sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
            <Stack spacing={0.5} sx={{ py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'black' }}>
                    Add to Mailing
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Search traders by commodity or company details, then add emails to a mailing list.
                </Typography>
            </Stack>

            <Stack spacing={2}>
                <SearchMailing
                    theme={theme}
                    type={SEARCH_TYPES.includes(type) ? type : ''}
                    query={query}
                    page={page}
                    loading={progress}
                    hasMore={!progress && traderCount(data) >= PAGE_SIZE}
                    onSearch={handleSearch}
                    onPageChange={handlePageChange}
                    onFakeSearch={handleFakeSearch}
                />

                <Paper elevation={0} sx={cardSx}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ mb: 2 }}
                    >
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Search results
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {progress
                                    ? 'Checking companies. Verified results are ready within 45 seconds.'
                                    : hasActiveSearch
                                        ? 'Select a company to add its deliverable emails to a mailing list.'
                                        : 'Run a search to see matching companies here.'}
                            </Typography>
                        </Box>
                    </Stack>

                    <TableComponent
                        apiData={data}
                        noResults={!progress && hasActiveSearch && !hasRows}
                        progress={progress}
                        handleAdd={handleAddTrader}
                        theme={theme}
                        hasActiveSearch={hasActiveSearch}
                    />
                </Paper>
            </Stack>

            <ModalComponent
                title={"Add to Mailing List"}
                subtitle="Choose a mailing list and confirm the emails to add."
                open={open}
                handleOpen={handleCloseModal}
                theme={theme}
                maxWidth={640}
            >
                <EmailList
                    theme={theme}
                    trader={selectedTrader}
                    handleCloseModal={handleCloseModal}
                    onEmailStateChange={patchTraderEmailState}
                />
            </ModalComponent>
        </Box>
    );
};

export default AddMailing;
